/**
 * Training Curriculum Service
 * Handles public and admin API access for fixed-node training curriculum assignments.
 */

import api from './apiClient';

/**
 * @typedef {Object} TrainingCurriculumResourceSummary
 * @property {number} id
 * @property {string} name
 * @property {boolean} isPublished
 * @property {number|null} [countryId]
 * @property {string|null} [countryName]
 * @property {string|null} [countryLanguage]
 */

/**
 * @typedef {Object} TrainingCurriculumNodeAssignment
 * @property {string} curriculumType
 * @property {string} nodeKey
 * @property {TrainingCurriculumResourceSummary|null} trainingProfile
 * @property {TrainingCurriculumResourceSummary|null} quizCollection
 * @property {TrainingCurriculumResourceSummary|null} learningPath
 * @property {TrainingCurriculumResourceSummary|null} survey
 * @property {string} dateModified
 * @property {boolean} hasAnyAssignment
 */

/**
 * @typedef {Object} TrainingCurriculumCountryAssignments
 * @property {number} countryId
 * @property {string} curriculumType
 * @property {string} countryName
 * @property {string} countryLanguage
 * @property {TrainingCurriculumNodeAssignment[]} items
 * @property {number} assignedNodeCount
 */

/**
 * @typedef {Object} TrainingCurriculumAssignmentLibraries
 * @property {TrainingCurriculumResourceSummary[]} trainingProfiles
 * @property {TrainingCurriculumResourceSummary[]} quizCollections
 * @property {TrainingCurriculumResourceSummary[]} learningPaths
 * @property {TrainingCurriculumResourceSummary[]} surveys
 */

/**
 * Load public curriculum assignments for one country and curriculum type.
 * @param {number} countryId - Country ID
 * @param {string} curriculumType - Curriculum audience type
 * @param {Object} [options] - Request options
 * @returns {Promise<TrainingCurriculumCountryAssignments>}
 */
export const getPublicAssignments = async (countryId, curriculumType, options = {}) => {
  return api.get('/training-curriculum/public', { countryId, curriculumType }, options);
};

/**
 * Load admin curriculum assignments for one country and curriculum type.
 * @param {number} countryId - Country ID
 * @param {string} curriculumType - Curriculum audience type
 * @param {Object} [options] - Request options
 * @returns {Promise<TrainingCurriculumCountryAssignments>}
 */
export const getAdminAssignments = async (countryId, curriculumType, options = {}) => {
  return api.get('/training-curriculum/admin', { countryId, curriculumType }, options);
};

/**
 * Load the published libraries available for assignment.
 * @param {Object} [options] - Request options
 * @returns {Promise<TrainingCurriculumAssignmentLibraries>}
 */
export const getAssignmentLibraries = async (options = {}) => {
  return api.get('/training-curriculum/admin/libraries', {}, options);
};

/**
 * Create or update one curriculum-node assignment.
 * @param {number} countryId - Country ID
 * @param {string} curriculumType - Curriculum audience type
 * @param {string} nodeKey - Stable curriculum node key
 * @param {Object} payload - Assignment payload
 * @param {number|null} [payload.trainingProfileId] - Training profile ID
 * @param {number|null} [payload.quizCollectionId] - Quiz collection ID
 * @param {number|null} [payload.learningPathId] - Learning path ID
 * @param {number|null} [payload.surveyId] - Survey ID
 * @param {Object} [options] - Request options
 * @returns {Promise<TrainingCurriculumNodeAssignment>}
 */
export const upsertAssignment = async (countryId, curriculumType, nodeKey, payload, options = {}) => {
  return api.put(`/training-curriculum/admin/assignments/${countryId}/${encodeURIComponent(nodeKey)}?curriculumType=${encodeURIComponent(curriculumType)}`, payload, options);
};

/**
 * Delete one curriculum-node assignment.
 * @param {number} countryId - Country ID
 * @param {string} curriculumType - Curriculum audience type
 * @param {string} nodeKey - Stable curriculum node key
 * @param {Object} [options] - Request options
 * @returns {Promise<void>}
 */
export const deleteAssignment = async (countryId, curriculumType, nodeKey, options = {}) => {
  return api.delete(`/training-curriculum/admin/assignments/${countryId}/${encodeURIComponent(nodeKey)}?curriculumType=${encodeURIComponent(curriculumType)}`, options);
};

const trainingCurriculumApi = {
  getPublicAssignments,
  getAdminAssignments,
  getAssignmentLibraries,
  upsertAssignment,
  deleteAssignment,
};

export default trainingCurriculumApi;